import { useState } from "react";
import { Star, Loader2, ImagePlus, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewForm = ({ open, onOpenChange }) => {
  const { t } = useShop();
  const [form, setForm] = useState({ name: "", rating: 0, comment: "" });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const reset = () => {
    setForm({ name: "", rating: 0, comment: "" });
    setPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t.reviews.photoHint);
      return;
    }
    setPhoto(file);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) return;
    setSending(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("rating", String(form.rating));
      data.append("comment", form.comment);
      if (photo) data.append("photo", photo);
      await axios.post(`${API}/reviews`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t.reviews.success);
      reset();
      onOpenChange(false);
    } catch {
      toast.error(t.reviews.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-cream border-brown/10 text-brown sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="review-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium text-brown">
            {t.reviews.formTitle}
          </DialogTitle>
          <DialogDescription className="text-sm font-light text-brown-light">
            {t.reviews.formSub}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-7" data-testid="review-form">
          <div>
            <label htmlFor="review-name" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
              {t.reviews.name}
            </label>
            <Input
              id="review-name"
              required
              minLength={2}
              maxLength={120}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="editorial-input mt-1"
              data-testid="review-name-input"
            />
          </div>

          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-brown-muted mb-2">
              {t.reviews.rating}
            </p>
            <div className="flex gap-1" data-testid="review-rating-selector">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, rating: n }))}
                  className={`p-1 transition-colors duration-200 ${
                    form.rating >= n ? "text-olive-dark" : "text-brown/20 hover:text-brown/40"
                  }`}
                  aria-label={`${n} star`}
                  data-testid={`review-star-${n}`}
                >
                  <Star size={26} fill={form.rating >= n ? "currentColor" : "none"} strokeWidth={1.5} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="review-comment" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
              {t.reviews.comment}
            </label>
            <Textarea
              id="review-comment"
              required
              minLength={10}
              maxLength={1000}
              rows={4}
              placeholder={t.reviews.commentPlaceholder}
              value={form.comment}
              onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
              className="editorial-input mt-1 resize-none placeholder:text-brown/30"
              data-testid="review-comment-input"
            />
          </div>

          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-brown-muted mb-2">
              {t.reviews.photo}
            </p>
            {photoPreview ? (
              <div className="relative w-28 h-28 overflow-hidden border border-brown/10">
                <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => {
                    setPhoto(null);
                    URL.revokeObjectURL(photoPreview);
                    setPhotoPreview(null);
                  }}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-brown/80 text-cream flex items-center justify-center"
                  aria-label="Remove photo"
                  data-testid="review-photo-remove"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <label
                className="inline-flex items-center gap-3 px-5 py-3 border border-dashed border-brown/25 text-sm text-brown-light hover:border-olive hover:text-olive-dark cursor-pointer transition-colors duration-300"
                data-testid="review-photo-upload"
              >
                <ImagePlus size={17} strokeWidth={1.5} /> {t.reviews.choosePhoto}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              </label>
            )}
            <p className="mt-2 text-xs text-brown-muted font-light">{t.reviews.photoHint}</p>
          </div>

          <button
            type="submit"
            disabled={sending || form.rating === 0}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-brown text-cream text-sm font-medium tracking-wide hover:bg-olive-dark disabled:opacity-60 transition-colors duration-300"
            data-testid="review-submit-button"
          >
            {sending && <Loader2 size={16} className="animate-spin" />}
            {sending ? t.reviews.sending : t.reviews.submit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewForm;
